export type GfxPerformancePhase =
  | 'block-lit-update'
  | 'canvas-render'
  | 'canvas-element-render'
  | 'canvas-element-visibility'
  | 'canvas-layer-render'
  | 'canvas-layer-setup'
  | 'canvas-overlay-render'
  | 'canvas-refresh-request'
  | 'database-presence-decoration'
  | 'database-presence-project'
  | 'database-body-contract'
  | 'database-height-sync'
  | 'database-presentation-sync'
  | 'database-projection'
  | 'database-view-render'
  | 'database-viewport-sync'
  | 'dom-render'
  | 'edge-full-render'
  | 'edge-viewport-transform'
  | 'note-content-mutation'
  | 'note-content-resize'
  | 'note-render'
  | 'note-updated'
  | 'viewport-cull'
  | 'viewport-dom-transform'
  | 'viewport-initial-render'
  | 'viewport-style-layout-flush'
  | 'viewport-update';

export type GfxPerformanceSample = {
  detail?: Record<string, boolean | number | string | null>;
  durationMs: number;
  itemCount?: number;
  phase: GfxPerformancePhase;
  scope: object;
};

type GfxPerformanceListener = (sample: GfxPerformanceSample) => void;

const listeners = new Set<GfxPerformanceListener>();

export function isGfxPerformanceMeasurementActive(): boolean {
  return listeners.size > 0;
}

function emitGfxPerformanceSample(sample: GfxPerformanceSample): void {
  if (listeners.size === 0) return;
  listeners.forEach(listener => listener(sample));
}

export function measureGfxPerformance<T>(
  scope: object,
  phase: GfxPerformancePhase,
  operation: () => T,
  getItemCount?: () => number,
  getDetail?: () => GfxPerformanceSample['detail']
): T {
  if (listeners.size === 0) return operation();

  const start = performance.now();
  try {
    return operation();
  } finally {
    emitGfxPerformanceSample({
      durationMs: performance.now() - start,
      detail: getDetail?.(),
      itemCount: getItemCount?.(),
      phase,
      scope,
    });
  }
}

export function subscribeGfxPerformanceSamples(
  listener: GfxPerformanceListener
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
